var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import { Carousel, EMPTY_TEXT, FileInput } from '@mitchell/react.component.ux';
import { TranslationService } from "../../../common";
import { IntelligentAssignmentSummary, IntelligentPhotoCaptureDamageDetectionModalConstants } from '../../constants';
import { IntelligentPhotoCaptureAssignmentSummaryStatus, IntelligentPhotoCaptureContextUpdateOperation, IntelligentPhotoCaptureEstimatorCaptureMode, IntelligentPhotoCaptureViewType } from '../../enums';
import { IntelligentPBEContainerComponent } from './IntelligentPBEContainerComponent';
import { StyledActionIcon, StyledActionLabel, StyledModalActionButton, StyledModalActionList, StyledModalContent, StyledModalGridInnerContainer, StyledModalGridPhotoCaptureItem } from './IntelligentPhotoCaptureEstimatorThumbnailGallery.style';
import { attachmentInfo, buildEstimatorCarouselItems, buildGalleryAttachmentLookups, buildPhotoReplacementInfo, getActiveVisibleSelection, getDeduplicatedEstimatorGalleryData, getEstimatorSummaryHeaderText, getFileUploadErrorMessage, getOrderedEstimatorGalleryEntries, getRetakeTargetSelection, getSafeGalleryCarouselIndex, getSafeInitialEstimatorCarouselIndex, readFileAsDataUrl } from '../../utilities';
export const IntelligentPhotoCaptureEstimatorThumbnailGallery = ({ appTheme, capturedPhoto, companyCode, contextId, countryCode, estimatorWorker, fileSizeLimit, galleryAttachments, galleryUploadAttachments, initialSelectedTileId, isConfidence, isReviewMode, photoCaptureAttachmentInfo, prtcDetailsData, tempAttachmentInfo, userVehicleInfo, vehicle, onDeleteAttachments, onDeleteAllAttachmentIterations, onDeleteGalleryAttachment, onResetCapturedPhoto, onResetGalleryAttachment, onResetToast, onUploadGalleryAttachment, onViewChange, onShowToast }) => {
    const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);
    const [fileUploadError, setFileUploadError] = React.useState(EMPTY_TEXT);
    const previousSafeInitialIndexRef = React.useRef(null);
    const autoRetakePreviewKeyRef = React.useRef('');
    const uploadInputRef = React.useRef(null);
    const lastDeletedIndexRef = React.useRef(null);
    const translateFn = TranslationService.translate;
    const { CAROUSEL_ID, ACTIONS } = IntelligentPhotoCaptureDamageDetectionModalConstants;
    const { createAttachmentIteration } = estimatorWorker;
    const { deletedFileIdSet, replacementByFileId } = React.useMemo(() => buildGalleryAttachmentLookups(galleryAttachments), [galleryAttachments]);
    /**
     * Produces a sorted list of `{ tileId, attachment }` items.
     * In review mode only failed-quality attachments are included.
     * Ordering is determined by country code, vehicle, and confidence flag.
     */
    const orderedAttachmentItems = React.useMemo(() => {
        const normalizedAttachmentInfo = photoCaptureAttachmentInfo !== null && photoCaptureAttachmentInfo !== void 0 ? photoCaptureAttachmentInfo : [];
        // In review mode, show only failed quality items.
        const attachmentsToOrder = isReviewMode
            ? attachmentInfo.getFailedQualityAttachments(normalizedAttachmentInfo)
            : normalizedAttachmentInfo;
        return getOrderedEstimatorGalleryEntries(attachmentsToOrder, countryCode, vehicle, isConfidence, prtcDetailsData, userVehicleInfo);
    }, [countryCode, isConfidence, isReviewMode, photoCaptureAttachmentInfo, vehicle, prtcDetailsData, userVehicleInfo]);
    /**
     * Accepts `orderedAttachmentItems`, filters soft-deleted entries, deduplicates by
     * `fileId`, and converts directly to `ICarouselItem[]` ready for `<Carousel>`.
     * Also surfaces `galleryItems` and `tileIdToIndex` for active-selection and retake flows.
     */
    const { galleryItems, tileIdToIndex, previewCarouselItems } = React.useMemo(() => {
        const filteredAttachmentItems = !deletedFileIdSet.size
            ? orderedAttachmentItems
            : orderedAttachmentItems.filter((item) => !deletedFileIdSet.has(item.attachment.fileId));
        const { galleryItems, tileIdToIndex } = getDeduplicatedEstimatorGalleryData(filteredAttachmentItems);
        return {
            galleryItems,
            tileIdToIndex,
            previewCarouselItems: buildEstimatorCarouselItems(galleryItems, replacementByFileId)
        };
    }, [deletedFileIdSet, orderedAttachmentItems, replacementByFileId]);
    /**
     * Normalizes `index` to the valid carousel range `[0, totalSlides - 1]`.
     * Returns `0` when the carousel is empty to avoid out-of-bounds access.
     * Memoized on slide count so carousel change handlers stay referentially stable.
     */
    const getSafeCarouselIndex = React.useCallback((index) => {
        return getSafeGalleryCarouselIndex(index, previewCarouselItems.length);
    }, [previewCarouselItems.length]);
    /**
     * Resolves the initial carousel slide index from the `initialSelectedTileId` prop.
     * Uses `tileIdToIndex` to translate a tile ID to its carousel position, then
     * clamps the result to the available range.
     * Always returns `0` in review mode.
     */
    const safeInitialIndex = React.useMemo(() => {
        return getSafeInitialEstimatorCarouselIndex(initialSelectedTileId, isReviewMode, tileIdToIndex, previewCarouselItems.length);
    }, [tileIdToIndex, initialSelectedTileId, isReviewMode, previewCarouselItems.length]);
    React.useEffect(() => {
        const hasSafeInitialIndexChanged = previousSafeInitialIndexRef.current !== safeInitialIndex;
        previousSafeInitialIndexRef.current = safeInitialIndex;
        setSelectedImageIndex((prevIndex) => {
            if (lastDeletedIndexRef.current !== null) {
                const deletedIndex = lastDeletedIndexRef.current;
                lastDeletedIndexRef.current = null;
                if (previewCarouselItems.length === 0)
                    return 0;
                // If deleted last item and items remain, move to first item
                if (deletedIndex >= previewCarouselItems.length) {
                    return 0;
                }
                return deletedIndex;
            }
            if (hasSafeInitialIndexChanged) {
                // Re-initialize selection when incoming start index changes.
                return safeInitialIndex;
            }
            // Re-clamp after list size changes (for example, after delete).
            return getSafeCarouselIndex(prevIndex);
        });
    }, [getSafeCarouselIndex, safeInitialIndex, previewCarouselItems.length]);
    /**
     * Handles carousel slide changes from user navigation and clamps the
     * selected index to a safe in-range value.
     * @param index - Carousel index emitted by the slide change event.
     */
    const handleCarouselSlideChange = React.useCallback((index) => {
        setSelectedImageIndex(getSafeCarouselIndex(index));
    }, [getSafeCarouselIndex]);
    /**
     * Derives the `IPhotoCaptureAttachmentFileInfo` for the slide currently shown
     * in the carousel. Used by `handleDeleteActiveGalleryImage` to identify which file
     * to add to `deletedFileIds`. Returns `undefined` when the list is empty.
     */
    const activeVisibleSelection = React.useMemo(() => getActiveVisibleSelection(galleryItems, selectedImageIndex), [
        galleryItems,
        selectedImageIndex
    ]);
    const { attachment: activeVisibleAttachment, galleryItem: activeVisibleGalleryItem } = activeVisibleSelection;
    React.useEffect(() => {
        const retakeFile = capturedPhoto === null || capturedPhoto === void 0 ? void 0 : capturedPhoto.file;
        if (!retakeFile) {
            return;
        }
        const retakeTargetSelection = getRetakeTargetSelection(initialSelectedTileId, tileIdToIndex, galleryItems, previewCarouselItems.length);
        if (!retakeTargetSelection) {
            return;
        }
        const { selectedTileId, targetCarouselIndex, targetGalleryItem, targetAttachment } = retakeTargetSelection;
        const autoRetakePreviewKey = `${selectedTileId}:${retakeFile.name}:${retakeFile.lastModified}:${retakeFile.size}`;
        if (autoRetakePreviewKeyRef.current === autoRetakePreviewKey) {
            return;
        }
        autoRetakePreviewKeyRef.current = autoRetakePreviewKey;
        void readFileAsDataUrl(retakeFile)
            .then((previewUrl) => {
            onUploadGalleryAttachment(buildPhotoReplacementInfo(targetAttachment, targetGalleryItem.tileId, IntelligentPhotoCaptureContextUpdateOperation.RETAKE, { file: retakeFile, previewUrl }));
            setSelectedImageIndex(getSafeCarouselIndex(targetCarouselIndex));
            onShowToast(IntelligentPhotoCaptureAssignmentSummaryStatus.RETAKE);
            onResetCapturedPhoto();
        })
            .catch(() => {
            // Optionally surface error to user here
            onResetCapturedPhoto();
        });
    }, [
        capturedPhoto,
        galleryItems,
        tileIdToIndex,
        getSafeCarouselIndex,
        initialSelectedTileId,
        previewCarouselItems.length,
        onUploadGalleryAttachment,
        onShowToast,
        onResetCapturedPhoto
    ]);
    // Soft-delete the active photo capture image from this gallery session.
    const handleDeleteActiveGalleryImage = React.useCallback(() => {
        if (!activeVisibleAttachment || !activeVisibleGalleryItem || !activeVisibleAttachment.fileId) {
            return;
        }
        // Store the index of the item being deleted
        lastDeletedIndexRef.current = selectedImageIndex;
        onDeleteGalleryAttachment(buildPhotoReplacementInfo(activeVisibleAttachment, activeVisibleGalleryItem.tileId, IntelligentPhotoCaptureContextUpdateOperation.DELETE));
        onShowToast(IntelligentPhotoCaptureAssignmentSummaryStatus.DELETE);
    }, [activeVisibleAttachment, activeVisibleGalleryItem, onDeleteGalleryAttachment, onShowToast, selectedImageIndex]);
    /**
     * Opens the gallery upload picker when called with no arguments.
     * When called with a FileList (from the file input's onSelected), reads the
     * selected file and applies a soft replacement to the active gallery image.
     * @param fileList - Optional file selection received from the gallery upload input.
     * @returns Promise that resolves after preview state has been updated.
     */
    const handleUploadActionClick = React.useCallback((fileList) => __awaiter(void 0, void 0, void 0, function* () {
        setFileUploadError(EMPTY_TEXT);
        if (!fileList) {
            if (uploadInputRef.current) {
                uploadInputRef.current.click();
            }
            return;
        }
        try {
            const file = fileList.item(0);
            if (!file || !activeVisibleAttachment || !activeVisibleAttachment.fileId || !activeVisibleGalleryItem) {
                return;
            }
            const fileUploadErrorMessage = getFileUploadErrorMessage(file, fileSizeLimit);
            if (fileUploadErrorMessage) {
                setFileUploadError(fileUploadErrorMessage);
                return;
            }
            const previewUrl = yield readFileAsDataUrl(file);
            onUploadGalleryAttachment(buildPhotoReplacementInfo(activeVisibleAttachment, activeVisibleGalleryItem.tileId, IntelligentPhotoCaptureContextUpdateOperation.UPLOAD, { file, previewUrl }));
            onShowToast(IntelligentPhotoCaptureAssignmentSummaryStatus.REPLACE);
        }
        catch (_a) {
            return;
        }
    }), [activeVisibleAttachment, activeVisibleGalleryItem, onUploadGalleryAttachment, fileSizeLimit, onShowToast]);
    /**
     * Starts retake flow for estimator gallery action.
     * Opens estimator camera view for a retake.
     * This does not trigger upload from the gallery itself.
     */
    const handleRetakeActionClick = React.useCallback(() => {
        onResetCapturedPhoto();
        onViewChange(IntelligentPhotoCaptureViewType.ESTIMATOR_CAMERA, isReviewMode ? IntelligentAssignmentSummary.REVIEW : EMPTY_TEXT, activeVisibleGalleryItem === null || activeVisibleGalleryItem === void 0 ? void 0 : activeVisibleGalleryItem.tileId);
    }, [activeVisibleGalleryItem === null || activeVisibleGalleryItem === void 0 ? void 0 : activeVisibleGalleryItem.tileId, isReviewMode, onResetCapturedPhoto, onViewChange]);
    const exitGallery = React.useCallback(() => {
        onResetToast();
        onResetGalleryAttachment();
        onResetCapturedPhoto();
        onViewChange(IntelligentPhotoCaptureViewType.NONE);
    }, [onResetCapturedPhoto, onResetGalleryAttachment, onResetToast, onViewChange]);
    const handleDoneClick = React.useCallback(() => {
        const attachmentsToDelete = galleryAttachments.map((attachment) => ({
            id: attachment.replacedAttachmentId,
            fileId: attachment.replacedFileId
        }));
        const attachmentsToUpload = galleryUploadAttachments
            .map((attachment) => attachment.file)
            .filter((file) => !!file);
        if (attachmentsToDelete.length > 0) {
            onDeleteAttachments(attachmentsToDelete, contextId, companyCode);
            if (attachmentsToDelete.length === tempAttachmentInfo.flat().length) {
                onDeleteAllAttachmentIterations();
            }
        }
        if (attachmentsToUpload.length > 0 && createAttachmentIteration) {
            createAttachmentIteration(attachmentsToUpload, IntelligentPhotoCaptureEstimatorCaptureMode.BULK_PHOTOS);
        }
        exitGallery();
    }, [
        galleryAttachments,
        galleryUploadAttachments,
        onDeleteAttachments,
        createAttachmentIteration,
        contextId,
        companyCode,
        tempAttachmentInfo,
        onDeleteAllAttachmentIterations,
        exitGallery
    ]);
    // Gallery action buttons
    const galleryActionButtons = React.useMemo(() => {
        const isDisabled = previewCarouselItems.length === 0;
        const actionConfig = [
            {
                id: ACTIONS.UPLOAD.ID,
                iconClass: ACTIONS.UPLOAD.ICON_CLASS,
                label: ACTIONS.UPLOAD.LABEL,
                onClick: handleUploadActionClick
            },
            {
                id: ACTIONS.RETAKE.ID,
                iconClass: ACTIONS.RETAKE.ICON_CLASS,
                label: ACTIONS.RETAKE.LABEL,
                onClick: handleRetakeActionClick
            },
            {
                id: ACTIONS.DELETE.ID,
                iconClass: ACTIONS.DELETE.ICON_CLASS,
                label: ACTIONS.DELETE.LABEL,
                onClick: handleDeleteActiveGalleryImage
            }
        ];
        return actionConfig.map(config => {
            let onClick = undefined;
            if (!isDisabled && typeof config.onClick === 'function') {
                if (config.id === ACTIONS.UPLOAD.ID) {
                    onClick = () => {
                        void config.onClick();
                    };
                }
                else {
                    onClick = config.onClick;
                }
            }
            return {
                id: config.id,
                iconClass: config.iconClass,
                label: translateFn(config.label),
                disabled: isDisabled,
                onClick
            };
        });
    }, [
        ACTIONS,
        handleDeleteActiveGalleryImage,
        handleRetakeActionClick,
        handleUploadActionClick,
        translateFn,
        previewCarouselItems.length
    ]);
    return (React.createElement(IntelligentPBEContainerComponent, { islanguageHide: true, appTheme: appTheme, warningMessage: fileUploadError, isFooter: true, isBtnDisabled: galleryAttachments.length === 0, btnLabel: translateFn(IntelligentPhotoCaptureDamageDetectionModalConstants.DONE_BUTTON_LABEL), estimatorSummaryHeaderText: getEstimatorSummaryHeaderText(userVehicleInfo !== null && userVehicleInfo !== void 0 ? userVehicleInfo : vehicle), movePrevious: exitGallery, moveNext: handleDoneClick, shouldShowAvatar: false },
        React.createElement(StyledModalContent, null,
            React.createElement(FileInput, { id: "estimator-thumbnail-gallery-file-input", element: React.createElement("span", { id: "estimator-thumbnail-gallery-upload-element", ref: uploadInputRef }), acceptedMimeTypes: "image/*", multiple: false, disabled: false, onSelected: (fileList) => {
                    void handleUploadActionClick(fileList);
                } }),
            React.createElement(StyledModalGridInnerContainer, { container: true },
                React.createElement(StyledModalGridPhotoCaptureItem, { item: true, sm: 12, md: 12 },
                    React.createElement(Carousel, { id: CAROUSEL_ID, items: previewCarouselItems, selectedIndex: selectedImageIndex, currentSlide: selectedImageIndex, onChange: handleCarouselSlideChange, isZoomEnabled: true, isDocumentPreviewEnabled: false, isEarEnabled: false }))),
            React.createElement(StyledModalActionList, null, galleryActionButtons.map((button) => (React.createElement(StyledModalActionButton, { key: button.id, disabled: button.disabled, onClick: button.onClick },
                React.createElement(StyledActionIcon, { className: button.iconClass }),
                React.createElement(StyledActionLabel, null, button.label))))))));
};
//# sourceMappingURL=IntelligentPhotoCaptureEstimatorThumbnailGallery.js.map